/**
 * Typed wrappers around the /api/issue-reports + /api/admin/analytics endpoints (Phase 1 backend).
 *
 * Mirrors the services/*.ts pattern: axios client, throw ApiError on non-2xx. The create call posts
 * multipart/form-data (scalar context fields + repeated `Images` parts) following the
 * uploadLogsheetDocumentRequest convention.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  CreateIssueReportInput,
  IssueReportDetailDto,
  IssueReportListItemDto,
  IssueReportListQuery,
  UpdateIssueStatusBody,
  UsageAnalyticsDto,
} from '@/types/issue-reports';
import type { PaginatedResult } from '@/types/roles';

function buildListUrl(q: IssueReportListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.status !== undefined) params.set('status', q.status);
  if (q.severity !== undefined) params.set('severity', q.severity);
  if (q.category !== undefined) params.set('category', q.category);
  params.set('page', String(q.page ?? 1));
  params.set('pageSize', String(q.pageSize ?? 20));
  return `/api/issue-reports?${params.toString()}`;
}

/** Create a report (open to any authenticated user). Posts multipart with 0..N image parts. */
export async function createIssueReportRequest(
  input: CreateIssueReportInput,
  images: readonly File[],
  onProgress?: (percent: number) => void,
): Promise<IssueReportDetailDto> {
  try {
    const form = new FormData();
    form.append('Description', input.description);
    form.append('Severity', input.severity);
    form.append('Category', input.category);
    form.append('PageUrl', input.context.pageUrl);
    form.append('RoutePath', input.context.routePath);
    form.append('UserAgent', input.context.userAgent);
    form.append('Browser', input.context.browser);
    form.append('OperatingSystem', input.context.operatingSystem);
    form.append('ViewportWidth', String(input.context.viewportWidth));
    form.append('ViewportHeight', String(input.context.viewportHeight));
    form.append('ClientReportedAtUtc', input.context.clientReportedAtUtc);
    form.append('ConsoleErrorsJson', JSON.stringify(input.consoleErrors));
    form.append('NetworkErrorsJson', JSON.stringify(input.networkErrors));
    for (const file of images) {
      form.append('Images', file);
    }

    const r = await api.request<IssueReportDetailDto>({
      url: '/api/issue-reports',
      method: 'post',
      data: form,
      transformRequest: (d, headers) => {
        (headers as { delete?: (k: string) => unknown }).delete?.('Content-Type');
        delete (headers as Record<string, unknown>)['Content-Type'];
        delete (headers as Record<string, unknown>)['content-type'];
        return d as unknown;
      },
      onUploadProgress: (evt) => {
        if (onProgress !== undefined && typeof evt.total === 'number' && evt.total > 0) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    });
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** Super-Admin: paginated, filterable list. */
export async function listIssueReportsRequest(
  query: IssueReportListQuery = {},
): Promise<PaginatedResult<IssueReportListItemDto>> {
  try {
    const { data } = await api.get<PaginatedResult<IssueReportListItemDto>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** Super-Admin: full detail incl. captured context + attachment metadata. */
export async function getIssueReportRequest(id: number): Promise<IssueReportDetailDto> {
  try {
    const { data } = await api.get<IssueReportDetailDto>(`/api/issue-reports/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** Super-Admin: fetch an attachment's bytes as a Blob (authorized via the axios bearer interceptor). */
export async function getIssueAttachmentBlobRequest(id: number, attachmentId: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `/api/issue-reports/${String(id)}/attachments/${String(attachmentId)}`,
      { responseType: 'blob' },
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** Super-Admin: update triage status (+ optional notes). */
export async function updateIssueStatusRequest(
  id: number,
  body: UpdateIssueStatusBody,
): Promise<IssueReportDetailDto> {
  try {
    const { data } = await api.patch<IssueReportDetailDto>(
      `/api/issue-reports/${String(id)}/status`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** Super-Admin: soft-delete a report. */
export async function deleteIssueReportRequest(id: number): Promise<void> {
  try {
    await api.delete(`/api/issue-reports/${String(id)}`);
  } catch (e) {
    throw toApiError(e);
  }
}

/** Super-Admin: usage / analytics snapshot. */
export async function getUsageAnalyticsRequest(): Promise<UsageAnalyticsDto> {
  try {
    const { data } = await api.get<UsageAnalyticsDto>('/api/admin/analytics/usage');
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
